import type { PropsWithChildren } from 'react';

import { useCanGoBack, useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';

import { Breadcrumbs, Button, Text } from '@/_components';
import { hotKeys, useShortcutKey } from '@/_shared';
import type { RouteListItem, ToPath } from '@/types';

export type THeaderWithBack = { title: string; routes?: RouteListItem[]; backLink?: ToPath };

export function LeftHeaderWithBack({ title, children, routes, backLink }: PropsWithChildren<THeaderWithBack>) {
  const nav = useNavigate();
  const { state } = useLocation();
  const { history } = useRouter();
  const canGoBack = useCanGoBack();

  const handleBackButton = () => {
    if (state?.naturalBack) {
      history.back();
    } else if (backLink && (backLink.toString() === '/settings' || !state.key || state?.isFromNew)) {
      nav({ to: backLink });
    } else if (canGoBack) {
      history.back();
    } else if (backLink) {
      nav({ to: backLink });
    }
  };

  const { osKey } = useShortcutKey(hotKeys.goBack, () => {
    handleBackButton();
  });

  return (
    <>
      <section>
        <div className="flex items-center gap-4">
          {(backLink || state?.naturalBack) && (
            <div className="group relative cursor-pointer">
              <Button
                as="link"
                size="sm"
                icon={ChevronLeft}
                className="text-accent-light border-c-border/60 group-hover:text-accent-light dark:group-hover:text-accent-x-light border pr-8 pl-px group-hover:bg-gray-300 dark:group-hover:bg-gray-950/60"
                iconClassName="size-6"
                aria-label="Back button"
                onClick={handleBackButton}
              />
              <div className="text-c-card-muted peer border-outline pointer-events-none absolute right-2.5 bottom-3 flex h-3 cursor-pointer items-center justify-center rounded border text-xs leading-0 opacity-70 dark:border-gray-600">
                {osKey}←
              </div>
            </div>
          )}

          <Text as="h4" className="text-c-card-foreground font-black dark:text-white">
            {title}
          </Text>
        </div>

        {routes && routes?.length > 2 && (
          <div className="mt-0.5">
            <Breadcrumbs pages={routes} />
          </div>
        )}
      </section>
      {children}
    </>
  );
}
